import TableHeader from "@commons/Table/TableHeader";
import Table from "@commons/Table/TableContent";
import AddIcon from "@mui/icons-material/Add";
import { usePage } from "@hooks/usePage";
import { useParams } from "react-router-dom";
import { useGetVisits } from "@services/visits/list";
import { config } from "@/config";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { VisitsColumns } from "@typings/entities/visit/table";
import {
  prepareVisitsTable,
  type VisitItemListWithLabels,
} from "@utils/translator/visits";
import DetailsModalVisit from "./modal/DetailsModalVisit";
import { useExportVisits } from "@hooks/exports/useExportVisits";
import { usePermissions } from "@hooks/usePermissions";
import type { actionsType } from "@typings/components/table";

function TableViewVisit() {
  const { openModal, setDataModal } = usePage();
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(config.table.defaultPageSize);
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);
  const { handleExport, isLoading: isExporting } = useExportVisits();
  const { can } = usePermissions();

  const {
    error,
    isLoading,
    data: visits,
  } = useGetVisits({
    locationId: id ?? "",
    limit: rowsPerPage,
    page: page + 1,
  });

  const heads = VisitsColumns;

  const handleEdit = (visit: VisitItemListWithLabels) => {
    setDataModal({ id: visit.id });
    openModal("editVisit");
  };

  const handleExportVisits = (locationId?: string) => async () => {
    handleExport(locationId);
  };

  const handleView = (visit: VisitItemListWithLabels) => {
    setSelectedVisitId(visit.id);
    openModal("detailsVisit");
  };

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  if (error) {
    toast.error("Erro ao carregar as visitas");
  }

  const preparedVisits = prepareVisitsTable(visits?.items || []);

  const canCreateVisit = can("create", "visits");
  const canUpdateVisit = can("update", "visits");

  const actions = ["view"] as actionsType;
  if (canUpdateVisit) {
    actions.push("edit");
  }

  return (
    <div className="w-full">
      <div>
        <TableHeader.Root>
          {canCreateVisit && (
            <TableHeader.Button
              customVariant="secondary"
              startIcon={<AddIcon />}
              onClick={() => openModal("newVisit")}
            >
              Registrar Visita
            </TableHeader.Button>
          )}
          <TableHeader.Button
            customVariant="dark"
            onClick={handleExportVisits(id)}
            disabled={isExporting}
          >
            {isExporting ? "Exportando..." : "Exportar .csv"}
          </TableHeader.Button>
        </TableHeader.Root>

        <Table.Root isLoading={isLoading}>
          <Table.Head heads={heads} actions={true} />
          <Table.Body<VisitItemListWithLabels>
            rows={preparedVisits}
            columns={heads.map((head) => head.id)}
            actions={actions}
            onView={handleView}
            onEdit={handleEdit}
          />
          <Table.Footer
            colSpan={heads.length}
            count={visits?.meta.totalItems ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Table.Root>
      </div>

      {selectedVisitId && <DetailsModalVisit visitId={selectedVisitId} />}
    </div>
  );
}

export default TableViewVisit;
